/**
 * Design Lab Init Machine
 * Single machine handling all initialization flows
 */

import { createMachine, assign } from "xstate";
import type {
  ApolloClientType,
  DesignLabInitContext,
  DesignLabInitEvent,
  ParsedUrlParams
} from "./design-lab-init.types";

// Initial context (apolloClient provided via input)
const initialContext: Omit<DesignLabInitContext, "apolloClient"> = {
  urlParams: {} as ParsedUrlParams,
  isEditMode: false,
  flowType: null,
  project: null,
  latestProject: null,
  composition: null,
  uploadSource: null,
  targetSide: "front" as const,
  showModal: false,
  modalAction: null,
  // MN-5326: defaults preserve legacy behavior — input from useDesignLabInit
  // overrides legacyAlwaysShowModal to false when the flip is on.
  hasCookieArtwork: false,
  legacyAlwaysShowModal: true
};

export const designLabInitMachine = createMachine({
  id: "designLabInit",
  initial: "parsing",
  types: {} as {
    context: DesignLabInitContext;
    events: DesignLabInitEvent;
    input: {
      apolloClient: ApolloClientType;
      navigate?: (path: string, options?: { replace?: boolean }) => void;
      hasCookieArtwork?: boolean;
      legacyAlwaysShowModal?: boolean;
    };
  },
  context: ({ input }) => ({
    ...initialContext,
    apolloClient: input?.apolloClient,
    ...(input?.navigate && { navigate: input.navigate }),
    ...(typeof input?.hasCookieArtwork === "boolean" && {
      hasCookieArtwork: input.hasCookieArtwork
    }),
    ...(typeof input?.legacyAlwaysShowModal === "boolean" && {
      legacyAlwaysShowModal: input.legacyAlwaysShowModal
    })
  }),

  states: {
    parsing: {
      description: "Parse URL parameters and determine flow",
      entry: "parseUrlParams",
      always: [
        {
          guard: "isEditMode",
          target: "editModeDetected",
          actions: "setEditMode"
        },
        {
          guard: "isExistingProject",
          target: "loadingProject",
          actions: "setFlowTypeExisting"
        },
        {
          guard: "isAutoCreate",
          target: "creatingProject",
          actions: "setFlowTypeCreate"
        },
        {
          target: "checkingLatestProject",
          actions: "setFlowTypeModal"
        }
      ]
    },

    editModeDetected: {
      description: "Edit mode detected - stop here, let edit mode hook handle",
      type: "final",
      entry: assign({ isEditMode: true })
    },

    loadingProject: {
      description: "Load existing project by UUID",
      invoke: {
        id: "loadProject",
        src: "loadProject",
        input: ({ context }) => ({
          uuid: context.urlParams.uuid!,
          apolloClient: context.apolloClient!,
          sample: context.urlParams.sample ?? false
        }),
        onDone: {
          target: "checkingComposition",
          actions: ["setProject", "navigateToProjectUrl"]
        },
        onError: {
          // Even if loading fails, mark as ready since Apollo cache might still work
          target: "ready",
          actions: "setError"
        }
      }
    },

    checkingLatestProject: {
      description: "Load latest project; modal display is gated on saved-content + flip (MN-5326)",
      // No eager `entry: assign({ showModal: true })` — modal display is now
      // decided by the branched onDone below. This prevents the empty-skeleton
      // flash for first-time users when the design_lab_skip_empty_modal flip
      // is on, while the legacy branch preserves today's behavior when off.
      invoke: {
        id: "loadLatestProject",
        src: "loadLatestProject",
        input: ({ context }) => ({
          apolloClient: context.apolloClient!
        }),
        onDone: [
          {
            // Flip on AND no saved content (no GraphQL project, no guest cookie)
            // → skip the modal, go straight to creating a fresh project. This is
            // the state the user reaches today by clicking "My Device" inside
            // the modal — we just elide the redundant click.
            guard: "shouldSkipModalAndCreate",
            target: "creatingProject",
            actions: ["setLatestProject", "setFlowTypeCreate"]
          },
          {
            // Default branch: either the flip is off (legacy) OR the flip is on
            // and the user has saved content. Either way, the modal opens —
            // matching today's behavior in both cases.
            actions: ["setLatestProject", assign({ showModal: true })]
          }
        ],
        onError: {
          // Dead code in practice — services.ts:64-86 wraps loadLatestProject in
          // try/catch and returns null on failure — the onError branch does not
          // fire. Kept as a no-op safety net.
        }
      },
      on: {
        CONTINUE_WITH_LATEST: {
          target: "restoringProject",
          actions: assign({
            modalAction: "continue" as const,
            showModal: false
          })
        },
        START_NEW: {
          target: "creatingProject",
          actions: assign(({ event }) => ({
            modalAction: "new" as const,
            uploadSource: event.source,
            showModal: false
          }))
        },
        CLOSE_MODAL: {
          // Closing modal = continue with latest
          target: "restoringProject",
          actions: assign({
            showModal: false,
            modalAction: "continue" as const
          })
        }
      }
    },

    restoringProject: {
      description: "Restore latest project after modal",
      entry: [
        assign(({ context }) => ({
          project: context.latestProject
        })),
        "navigateToProjectUrl"
      ],
      always: [
        {
          guard: "hasProject",
          target: "checkingComposition"
        },
        {
          // No latest project, create new
          target: "creatingProject"
        }
      ]
    },

    creatingProject: {
      description: "Create new project",
      entry: "prepareProjectCreation",
      invoke: {
        id: "createProject",
        src: "createProject",
        input: ({ context }) => ({
          composition: context.composition,
          apolloClient: context.apolloClient!,
          sample: context.urlParams.sample ?? false
        }),
        onDone: [
          {
            guard: "hasUploadSource",
            target: "processingUpload",
            actions: ["setProject", "navigateToProjectUrl"]
          },
          {
            target: "ready",
            actions: ["setProject", "navigateToProjectUrl"]
          }
        ],
        onError: {
          target: "error",
          actions: "setError"
        }
      }
    },

    checkingComposition: {
      description: "Check if upload source needs to be processed",
      always: [
        {
          guard: "hasUploadSource",
          target: "processingUpload"
        },
        {
          target: "ready"
        }
      ]
    },

    processingUpload: {
      description: "Process upload workflows",
      initial: "determining",
      states: {
        determining: {
          description: "Determine upload type",
          always: [
            { guard: "isExternalUpload", target: "downloadingFiles" },
            { guard: "isLibraryUpload", target: "openingLibrary" },
            { guard: "isCanvaUpload", target: "openingCanva" },
            { guard: "isDeviceUpload", target: "waitingForFiles" },
            { target: "complete" } // No upload source
          ]
        },

        downloadingFiles: {
          description: "Download files from external URLs",
          invoke: {
            id: "downloadFiles",
            src: "downloadExternalFiles",
            input: ({ context }) => ({
              urls: context.externalUrls!,
              fileContentType: context.urlParams.fileContentType
            }),
            onDone: {
              target: "complete",
              actions: "setUploadedFiles"
            },
            onError: {
              target: "#designLabInit.error",
              actions: "setError"
            }
          }
        },

        openingLibrary: {
          description: "Design Library workflow",
          on: {
            LIBRARY_SELECTION: {
              target: "complete"
            },
            CANCEL: {
              target: "#designLabInit.ready"
            }
          }
        },

        openingCanva: {
          description: "Canva workflow",
          on: {
            CANVA_COMPLETE: {
              target: "complete"
            },
            CANCEL: {
              target: "#designLabInit.ready"
            }
          }
        },

        waitingForFiles: {
          description: "Device upload workflow - optional, can proceed without files",
          // Machine auto-proceeds after 100ms for machine to be in ready state so canSave can run
          after: {
            100: {
              target: "complete"
            }
          },
          on: {
            FILES_SELECTED: {
              target: "complete",
              actions: "setUploadedFiles"
            },
            CANCEL: {
              target: "#designLabInit.ready"
            }
          }
        },

        complete: {
          type: "final"
        }
      },

      onDone: {
        target: "ready",
        actions: [
          assign({
            uploadSource: null // Clear upload source when workflow completes
          }),
          "navigateToProjectUrl"
        ]
      }
    },

    ready: {
      description: "Initialization complete",
      type: "final"
    },

    error: {
      description: "Error state with retry capability",
      on: {
        RETRY: {
          target: "parsing",
          actions: "clearError"
        },
        RESET: {
          target: "parsing",
          actions: "resetContext"
        }
      }
    }
  }
});
